import { CheckCircle2 } from 'lucide-react'
import { useCallback } from 'react'
import { useAutoVerifyDomain, useDomainDetail } from '@/api/hooks/use-domains'
import { AutoConfigureCard } from '@/components/domains/auto-configure-card'
import { DomainRecordTables } from '@/components/domains/domain-record-tables'
import { VerificationCard } from '@/components/domains/verification-card'

interface DomainDetailPanelProps {
  domainId: string
  domainName: string
  status: string
}

export function DomainDetailPanel({
  domainId,
  domainName,
  status,
}: DomainDetailPanelProps): React.JSX.Element {
  const { data: detail, isLoading } = useDomainDetail(domainId, true)
  const isPending = status !== 'active'
  const autoVerify = useAutoVerifyDomain(domainId, isPending)

  // Check if verified from either the detail or auto-verify polling
  const isVerified =
    detail?.status === 'active' || autoVerify.data?.status === 'active' || status === 'active'

  const records = detail?.records ?? []

  const handleCheckNow = useCallback(() => {
    autoVerify.refetch()
  }, [autoVerify])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <span className="ml-2 text-sm text-text-secondary">Loading DNS records...</span>
      </div>
    )
  }

  if (records.length === 0) {
    return <p className="py-4 text-sm text-text-tertiary">No DNS records found.</p>
  }

  // Verified state: show success banner + records for reference
  if (isVerified) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium text-green-700">
            {domainName} is verified and active
          </span>
        </div>
        <DomainRecordTables records={records} />
      </div>
    )
  }

  // Pending state: full verification experience
  return (
    <div className="flex flex-col gap-6">
      {/* Auto-configure card */}
      <AutoConfigureCard domainId={domainId} onComplete={handleCheckNow} />

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-surface-3" />
        <span className="text-xs font-medium text-text-tertiary">Or add manually</span>
        <div className="h-px flex-1 bg-surface-3" />
      </div>

      {/* DNS record tables */}
      <DomainRecordTables records={records} />

      {/* Verification polling card */}
      <VerificationCard
        records={records}
        isFetching={autoVerify.isFetching}
        dataUpdatedAt={autoVerify.dataUpdatedAt}
        onCheckNow={handleCheckNow}
      />

      {/* DNS help link */}
      <a
        href="https://docs.one-email.com/guides/dns-setup"
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-text-tertiary hover:underline"
      >
        Need help? Read our DNS setup guide
      </a>
    </div>
  )
}
